import { useCallback, useEffect, useState } from "react";
import { KeyRound, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const input = "w-full px-3 py-2.5 rounded-lg border border-sage-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

const PinSetup = ({ pinSet, onDone }) => {
  const [cur, setCur] = useState("");
  const [pin, setPin] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/maintenance/pin", { current_pin: cur, new_pin: pin }); toast.success("PIN Super Admin disimpan"); setCur(""); setPin(""); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="mt-4 pt-4 border-t border-sage-200 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="superadmin-pin-form">
      {pinSet && <label className="text-xs font-semibold">PIN lama<input type="password" inputMode="numeric" value={cur} onChange={(e) => setCur(e.target.value)} className={input} data-testid="superadmin-pin-current-input" /></label>}
      <label className="text-xs font-semibold">{pinSet ? "PIN baru (6 angka)" : "Buat PIN (6 angka)"}<input type="password" inputMode="numeric" pattern="\d{6}" maxLength={6} required value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className={input} data-testid="superadmin-pin-new-input" /></label>
      <button className="px-4 py-2.5 rounded-full bg-sage-100 font-semibold text-sm flex items-center gap-1.5" data-testid="superadmin-pin-save-button"><KeyRound className="w-4 h-4" /> {pinSet ? "Ganti PIN" : "Simpan PIN"}</button>
    </form>
  );
};

export const MaintenancePanel = () => {
  const [st, setSt] = useState(null);
  const [minutes, setMinutes] = useState(20);
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const load = useCallback(() => { api.get("/admin/maintenance").then((r) => setSt(r.data)).catch(() => setSt(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(path, body); toast.success(msg); setPin(""); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return null;
  const until = st.active && new Date(st.until).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="card-paper p-6" data-testid="maintenance-panel">
      <h3 className="font-semibold text-lg flex items-center gap-2"><Wrench className="w-5 h-5" /> Update Sistem/Website</h3>
      <p className="text-sm text-muted2 mt-1">Selama mode update, pelanggan melihat halaman "Sedang update" dengan hitung mundur dan tidak bisa memesan. Panel staf tetap berjalan.</p>
      <div className={`mt-4 rounded-lg px-3 py-2 text-sm font-semibold ${st.active ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`} data-testid="maintenance-status">
        {st.active ? `Mode update aktif sampai ${until} WIB` : "Sistem normal"}
      </div>
      {!st.pin_set ? (
        <><p className="text-sm mt-4">Buat PIN Super Admin dulu untuk mengaktifkan mode update.</p><PinSetup pinSet={false} onDone={load} /></>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <label className="text-xs font-semibold">Durasi (menit)<input type="number" min={1} max={1440} value={minutes} onChange={(e) => setMinutes(e.target.value)} className={input} data-testid="maintenance-minutes-input" /></label>
            <label className="text-xs font-semibold">PIN Super Admin<input type="password" inputMode="numeric" placeholder="PIN" value={pin} onChange={(e) => setPin(e.target.value)} className={input} data-testid="maintenance-pin-input" /></label>
          </div>
          <label className="text-xs font-semibold block mt-3">Pesan untuk pelanggan (opsional)<input value={message} maxLength={200} onChange={(e) => setMessage(e.target.value)} placeholder="mis. Kami sedang menambah menu baru" className={input} data-testid="maintenance-message-input" /></label>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button disabled={busy || !pin} onClick={() => run("/admin/maintenance/start", { minutes: Number(minutes), pin, message }, `Mode update aktif ${minutes} menit`)} className="px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm disabled:opacity-60" data-testid="maintenance-start-button">{st.active ? "Perbarui Durasi" : "Aktifkan Mode Update"}</button>
            <button disabled={busy || !pin || !st.active} onClick={() => run("/admin/maintenance/stop", { pin }, "Sistem kembali normal")} className="px-4 py-2.5 rounded-lg text-sm font-semibold border border-sage-200 text-ink hover:bg-sage-100 disabled:opacity-50" data-testid="maintenance-stop-button">Matikan (Sistem Normal)</button>
            <button onClick={() => setShowPin((x) => !x)} className="ml-auto text-xs font-semibold text-forest" data-testid="superadmin-pin-toggle">Ganti PIN</button>
          </div>
          {showPin && <PinSetup pinSet onDone={() => { setShowPin(false); load(); }} />}
        </>
      )}
    </div>
  );
};
